import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import React from 'react';
import classNames from 'classnames';
import { divider, iconLight } from '../Styles/Colours';
import { SearchIcon } from './Icons';
import styles from './SearchInput.module.scss';

const popperModifiers = {
    flip: {
        enabled: false
    },
    offset: {
        offset: '6px, 0'
    },
    preventOverflow: {
        enabled: true,
        boundariesElement: 'window'
    },
    hide: {
        enabled: false
    },
    arrow: {
        enabled: false
    }
};

const popperStyles = { zIndex: 9999, minWidth: 375 };

type RenderSuggestionProps<T> = {
    suggestion: T;
    index: number;
    itemToString(item: T): string;
    itemToMenuKey(item: T): string;
    customItemRender?(item: T): React.ReactNode;
    onClick(item: T): void;
};

function renderSuggestion<T>({
    suggestion,
    itemToString,
    index,
    itemToMenuKey,
    customItemRender,
    onClick
}: RenderSuggestionProps<T>) {
    const handleClick = () => {
        return onClick(suggestion);
    };

    return (
        <MenuItem
            onMouseDown={handleClick}
            key={itemToMenuKey ? itemToMenuKey(suggestion) : itemToString(suggestion)}
            component='div'
            style={{
                borderBottom: `1px solid ${divider}`,
                paddingTop: 8,
                paddingBottom: 7,
                maxWidth: '100%',
                textOverflow: 'ellipsis',
                display: 'block'
            }}
            data-test-id={`singleSelect_menuItem_${index}`}
        >
            {customItemRender ? customItemRender(suggestion) : itemToString(suggestion)}
        </MenuItem>
    );
}

function dropDownMessage(message = 'No results found', testId: string) {
    return (
        <div key={testId} className={styles.dropDownMessage} data-test-id={testId}>
            {message}
        </div>
    );
}

const defaultResultToString = () => {
    throw new Error('Item to string handler missing');
};

const defaultResultClick = () => {
    throw new Error('Result clicked with no handler');
};

export enum InputModes {
    Standard = 'standard',
    Collapsible = 'collapsible'
}

export type SearchInputProps<T = any> = {
    inputId?: string;
    fullWidth?: boolean;
    placeholder?: string;
    mode?: InputModes; // default to standard
    maxResults?: number;
    noResultsMessage?: string;
    renderResult?(value: T): React.ReactNode;
    resultToString?(value: T): string;
    getResultKey?(value: T): string;
    onResultClick?(value: T): void;
    onFocus?(event: React.FocusEvent): void;
    onBlur?(event: React.FocusEvent): void;
    onKeyDown?(event: React.KeyboardEvent<HTMLInputElement>): void;
    onClear?(): void;
};

type SearchInputComponentProps<T = any> = SearchInputProps<T> & {
    value: string;
    testId: string;
    autoFocus?: boolean;
    noResults?: boolean;
    results?: T[];
    disablePortal?: boolean;
    onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
};

type SearchInputState = {
    focused: boolean;
    collapsed: boolean;
};

export class SearchInput<T = any> extends React.Component<SearchInputComponentProps<T>, SearchInputState> {
    public static getDerivedStateFromProps(props: SearchInputComponentProps, state: SearchInputState) {
        if (!props.value && !state.focused) {
            return {
                collapsed: true
            };
        }

        return null;
    }

    public state = {
        focused: false,
        collapsed: false
    };

    private input = React.createRef<HTMLInputElement>();

    private searchButton = React.createRef<HTMLDivElement>();

    private container = React.createRef<HTMLDivElement>();

    public render() {
        const mode = this.props.mode || InputModes.Standard;
        const className = classNames(styles.container, {
            [styles.dirty]: !!this.props.value,
            [styles.focused]: this.state.focused,
            [styles.fullWidth]: this.props.fullWidth,
            [styles.collapsed]: this.state.collapsed,
            [styles.standard]: mode === InputModes.Standard,
            [styles.collapsible]: mode === InputModes.Collapsible
        });
        const clearButtonClasses = {
            root: classNames(styles.clearButton, {
                [styles.hidden]: !this.props.value
            })
        };

        const width = this.container.current ? this.container.current.clientWidth : 0;
        const isOpen: boolean =
            this.state.focused && ((!!this.props.results && this.props.results.length > 0) || !!this.props.noResults);

        const menuStyles = { width, minWidth: 375, maxHeight: 400, overflow: 'auto' };

        const maxResults = this.props.maxResults || 10;

        const disablePortal = this.props.disablePortal !== undefined ? this.props.disablePortal : true;

        const NOOP = () => {
            return;
        };

        const changeMethod = this.props.onChange || NOOP;

        return (
            <div className={className} ref={this.container}>
                <div ref={this.searchButton} onClick={this.searchIconClick} className={styles.searchIcon}>
                    <SearchIcon size='32' color={iconLight} />
                </div>
                <div className={styles.searchComponents}>
                    <input
                        id={this.props.inputId}
                        autoFocus={this.props.autoFocus}
                        type='text'
                        ref={this.input}
                        data-lpignore='true'
                        value={this.props.value || ''}
                        placeholder={this.props.placeholder}
                        className={styles.inputField}
                        onChange={changeMethod}
                        onFocus={this.userFocused}
                        onBlur={this.userBlur}
                        onKeyDown={this.props.onKeyDown}
                        data-test-id={`${this.props.testId}-input-field`}
                    />
                    <Button
                        onClick={this.userClickedClear}
                        size='small'
                        disabled={!this.props.value}
                        data-test-id={`${this.props.testId}-input-button`}
                        classes={clearButtonClasses}
                    >
                        <Icon classes={{ root: styles.iconRoot }}>cancel</Icon>
                    </Button>
                </div>
                <Popper
                    open={isOpen}
                    anchorEl={this.container.current}
                    modifiers={popperModifiers}
                    style={popperStyles}
                    disablePortal={disablePortal}
                    data-test-id={`${this.props.testId}-input-results`}
                >
                    <Paper style={menuStyles} square>
                        {this.props.results &&
                            this.props.results
                                .filter((result: T, index) => index < maxResults)
                                .map((result: T, index: number) => {
                                    return renderSuggestion({
                                        suggestion: result,
                                        itemToString: this.props.resultToString || defaultResultToString,
                                        index,
                                        itemToMenuKey: this.props.getResultKey || defaultResultToString,
                                        customItemRender: this.props.renderResult,
                                        onClick: this.props.onResultClick || defaultResultClick
                                    });
                                })}
                        {this.props.noResults &&
                            dropDownMessage(this.props.noResultsMessage, `singleSelect_menuItem_no_results`)}
                        {this.props.results &&
                            this.props.results.length > maxResults &&
                            dropDownMessage(
                                'More results found - type to refine',
                                `singleSelect_menuItem_too_many_results`
                            )}
                    </Paper>
                </Popper>
            </div>
        );
    }

    private userFocused = (e: React.FocusEvent) => {
        this.setState(
            {
                focused: true,
                collapsed: false
            },
            () => {
                if (this.props.onFocus) this.props.onFocus(e);
            }
        );
    };

    private userBlur = (e: React.FocusEvent) => {
        if (this.props.onBlur) this.props.onBlur(e);

        // if blur event on the text input is triggered because the user clicked on the search icon,
        // ignore the blur event and let the search icon handle the click event to prevent
        // doubling up on handling of a single event from the user's perspective
        const target: any = e.relatedTarget || document.activeElement;
        if (target === this.searchButton) {
            return;
        }

        this.setState({
            focused: false
        });

        if (!this.props.value) {
            this.setState({
                collapsed: true
            });
        }
    };

    private searchIconClick = (e: any) => {
        this.setState(
            state => ({
                focused: !state.focused,
                collapsed: !state.collapsed
            }),
            () => {
                if (!this.state.focused || this.state.collapsed) {
                    this.setState({
                        focused: false,
                        collapsed: true
                    });
                    return;
                }

                if (this.input.current) this.input.current.focus();

                this.userFocused(e);
            }
        );
    };

    private userClickedClear = () => {
        this.setState(
            {
                collapsed: false
            },
            () => {
                if (this.props.onClear) this.props.onClear();

                if (this.input && this.input.current) this.input.current.focus();
            }
        );
    };
}
